import { useEffect, useState } from "react";
import { draftsApi, venturesApi } from "@/lib/api-client";
import { PenTool, Mail, Twitter, Megaphone, FileText, Check, X, Send, Loader2, Sparkles, CalendarRange } from "lucide-react";
import { relativeTime } from "@/lib/format";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const TYPE_ICONS = { email: Mail, social: Twitter, ad: Megaphone, blog: FileText };
const STATUS_COLOR = { pending: "#F59E0B", approved: "#22C55E", rejected: "#71717A", sent: "#3B82F6" };

export default function DraftingTable({ refreshKey }) {
  const [drafts, setDrafts] = useState([]);
  const [ventures, setVentures] = useState([]);
  const [ventureId, setVentureId] = useState("");
  const [focus, setFocus] = useState("");
  const [tab, setTab] = useState("pending");
  const [busy, setBusy] = useState(false);
  const [campaignBusy, setCampaignBusy] = useState(false);

  const load = async () => {
    try {
      const [d, v] = await Promise.all([draftsApi.list(), venturesApi.list()]);
      setDrafts(d); setVentures(v);
    } catch {}
  };
  useEffect(() => { load(); }, [refreshKey]);

  const runCreator = async () => {
    if (!ventureId) return toast.error("Select a venture");
    setBusy(true);
    try {
      const r = await draftsApi.createFromCreator(ventureId, focus);
      toast.success(`Creator drafted ${r.created.length} asset${r.created.length !== 1 ? "s" : ""}`);
      setFocus("");
      load();
    } catch { toast.error("Creator failed"); }
    finally { setBusy(false); }
  };

  const runCampaign = async () => {
    if (!ventureId) return toast.error("Select a venture");
    setCampaignBusy(true);
    try {
      const r = await draftsApi.bulkCampaign(ventureId, focus);
      toast.success(`7-day campaign staged for ${r.venture} across ${r.platforms.join(", ")}`);
      setFocus("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Campaign failed"); }
    finally { setCampaignBusy(false); }
  };

  const act = async (d, status) => {
    try {
      await draftsApi.update(d.id, { status });
      toast.success(`Draft ${status}`);
      load();
    } catch { toast.error("Update failed"); }
  };
  const remove = async (d) => {
    if (!window.confirm("Delete draft?")) return;
    await draftsApi.remove(d.id);
    load();
  };

  const filtered = drafts.filter((d) => (tab === "all" ? true : d.status === tab));
  const ventureName = (id) => ventures.find((v) => v.id === id)?.name || "—";

  return (
    <section className="uc-card p-6 uc-fade" data-testid="drafting-table">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="uc-chip mb-2"><PenTool size={11} className="text-pink-400"/> THE DRAFTING TABLE</div>
          <h2 className="font-serif-display text-2xl">Marketing Drafts · Awaiting Approval</h2>
          <p className="text-white/50 text-xs mt-1">Emails, posts, and ads Creator has staged across all ventures.</p>
        </div>
      </div>

      {/* Creator trigger */}
      <div className="p-4 border border-pink-500/25 bg-pink-500/[0.03] mb-4 grid grid-cols-1 md:grid-cols-7 gap-2 items-end" data-testid="creator-trigger">
        <div className="md:col-span-2">
          <label className="font-mono-uc text-[10px] uppercase tracking-wider text-white/50 block mb-1">Venture</label>
          <Select value={ventureId} onValueChange={setVentureId}>
            <SelectTrigger data-testid="creator-venture" className="h-10 rounded-none bg-black border-white/10">
              <SelectValue placeholder="Choose venture…"/>
            </SelectTrigger>
            <SelectContent className="bg-[#0F0F0F] border-white/10 text-white">
              {ventures.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-3">
          <label className="font-mono-uc text-[10px] uppercase tracking-wider text-white/50 block mb-1">Brief / Focus (optional)</label>
          <input
            data-testid="creator-focus"
            value={focus}
            onChange={(e) => setFocus(e.target.value)}
            placeholder="e.g. Q1 launch drop, angle: new-year momentum"
            className="w-full h-10 rounded-none bg-black border border-white/10 px-3 text-sm text-white/90 uc-focus font-mono-uc"
          />
        </div>
        <Button
          data-testid="creator-run-btn"
          onClick={runCreator}
          disabled={busy || campaignBusy || !ventureId}
          className="uc-btn-amber rounded-none h-10 text-xs"
          style={{ background: "#EC4899", color: "#fff" }}
        >
          {busy ? <Loader2 className="animate-spin mr-1" size={12}/> : <Sparkles size={12} className="mr-1"/>}
          Draft
        </Button>
        <Button
          data-testid="creator-campaign-btn"
          onClick={runCampaign}
          disabled={busy || campaignBusy || !ventureId}
          variant="ghost"
          className="rounded-none h-10 text-xs border border-pink-500/40 text-pink-400 hover:bg-pink-500/10 font-mono-uc uppercase tracking-wider"
        >
          {campaignBusy ? <Loader2 className="animate-spin mr-1" size={12}/> : <CalendarRange size={12} className="mr-1"/>}
          7-Day Campaign
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-transparent border-b border-white/10 rounded-none p-0 h-auto justify-start gap-4 mb-3 flex-wrap">
          {[["pending","Pending"],["approved","Approved"],["sent","Sent"],["rejected","Rejected"],["all","All"]].map(([k,label]) => (
            <TabsTrigger key={k} value={k} data-testid={`draft-tab-${k}`}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-pink-400 data-[state=active]:bg-transparent data-[state=active]:text-white text-white/50 px-0 pb-2 font-mono-uc text-[11px] uppercase tracking-widest">
              {label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value={tab} className="mt-0">
          <div className="space-y-2 max-h-[500px] overflow-y-auto uc-scroll -mr-2 pr-2">
            {filtered.map((d) => {
              const Icon = TYPE_ICONS[d.type] || FileText;
              const sc = STATUS_COLOR[d.status];
              return (
                <div key={d.id} className="border border-white/10 hover:border-white/20 transition-colors" style={{ borderLeft: `2px solid ${sc}` }} data-testid={`draft-${d.id}`}>
                  <div className="p-3">
                    <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <Icon size={13} className="text-pink-400 flex-shrink-0"/>
                        <span className="text-sm text-white font-medium truncate">{d.title}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono-uc text-[9px] uppercase tracking-wider" style={{ color: sc }}>{d.status}</span>
                      </div>
                    </div>
                    <div className="font-mono-uc text-[10px] text-white/40 uppercase tracking-wider mb-2">
                      {d.type} · {d.platform || "—"} · {ventureName(d.venture_id)} · {relativeTime(d.created_at)}
                    </div>
                    <div className="text-xs text-white/70 leading-relaxed whitespace-pre-wrap max-h-32 overflow-y-auto uc-scroll pr-1" data-testid={`draft-body-${d.id}`}>
                      {d.body}
                    </div>
                    {d.audience && <div className="font-mono-uc text-[10px] text-white/40 mt-2">Audience: {d.audience}</div>}
                    {d.status === "pending" && (
                      <div className="flex gap-2 mt-3 flex-wrap">
                        <button data-testid={`draft-approve-${d.id}`} onClick={() => act(d, "approved")} className="text-[10px] font-mono-uc uppercase tracking-wider px-2 py-1 border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 flex items-center gap-1">
                          <Check size={9}/> Approve
                        </button>
                        <button data-testid={`draft-send-${d.id}`} onClick={() => act(d, "sent")} className="text-[10px] font-mono-uc uppercase tracking-wider px-2 py-1 border border-blue-500/40 text-blue-400 hover:bg-blue-500/10 flex items-center gap-1">
                          <Send size={9}/> Mark Sent
                        </button>
                        <button data-testid={`draft-reject-${d.id}`} onClick={() => act(d, "rejected")} className="text-[10px] font-mono-uc uppercase tracking-wider px-2 py-1 border border-white/15 text-white/50 hover:bg-white/5 flex items-center gap-1">
                          <X size={9}/> Reject
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {filtered.length === 0 && <div className="text-white/40 text-xs italic py-8 text-center">No drafts in this bucket.</div>}
          </div>
        </TabsContent>
      </Tabs>
    </section>
  );
}
